import { FC, memo, useCallback, useMemo, useState } from 'react';

import {
  AlbumIcon,
  ArtistIcon,
  DeleteIcon,
  AddToPlaylist,
  AddToQueueIcon,
  AddToLibrary,
  AddedToLibrary,
  WorkIcon,
  RecordingIcon,
} from '../Icons';
import { Dropdown, MenuProps, Tooltip, message } from 'antd';

// Services
import { playlistService } from '../../services/playlists';

// Utils
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useComposerIdSet } from '../../utils/useComposerIdSet';
import { extractSpotifyArtistId, resolveArtistRoute } from '../../utils/resolveArtistRoute';
import { ensureComposerKnown, getCatalogIdForSpotifyId } from '../../utils/composerRegistry';
import { resolveTrackWorkId } from '../../utils/resolveWorkId';
import { resolveTrackRecordingId } from '../../utils/resolveRecordingId';
import { usePlayback } from '../../contexts/PlaybackContext';

// Interface
import type { Track } from '../../interfaces/track';
import type { Album } from '../../interfaces/albums';
import type { Playlist } from '../../interfaces/playlists';

// Redux
import { playlistActions } from '../../store/slices/playlist';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  fetchMyPlaylists,
  getUserPlaylists,
  yourLibraryActions,
} from '../../store/slices/yourLibrary';
import { userService } from '../../services/users';
import { likedSongsActions } from '../../store/slices/likedSongs';
import { spotifyActions } from '../../store/slices/spotify';
import { albumActions } from '../../store/slices/album';
import { artistActions } from '../../store/slices/artist';
import { Artist } from '../../interfaces/artist';
import { uiActions } from '../../store/slices/ui';

interface TrackActionsWrapperProps {
  canEdit?: boolean;
  saved?: boolean;
  album?: Album | null;
  artist?: Artist | null;
  onSavedToggle?: () => void;
  playlist?: Playlist | null;
  track: Track | Spotify.Track;
  trigger?: ('contextMenu' | 'click')[];
  children: React.ReactNode | React.ReactNode[];
}

export const TrackActionsWrapper: FC<TrackActionsWrapperProps> = memo((props) => {
  const { children, artist, track, playlist, canEdit, album, saved, onSavedToggle } = props;

  const { t } = useTranslation(['playlist']);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const myPlaylists = useAppSelector(getUserPlaylists);
  const userId = useAppSelector((state) => state.auth.user?.id);
  const currentSong = useAppSelector(
    (state) => state.spotify.state?.track_window?.current_track?.id
  );
  const composerIds = useComposerIdSet();
  const { queueSession, queueStatus, performQueueAction } = usePlayback();
  const [isQueueAppendPending, setIsQueueAppendPending] = useState(false);

  const handleUserValidation = useCallback(
    (button?: boolean) => {
      if (!userId) {
        dispatch(button ? uiActions.openLoginButton() : uiActions.openLoginTooltip());
        return false;
      }
      return true;
    },
    [dispatch, userId]
  );

  const handleQueueAppend = useCallback(async () => {
    if (isQueueAppendPending) return;
    if (!handleUserValidation(true)) return;
    if (!queueSession || queueStatus !== 'ready') {
      void message.info('Start playback to build a queue before adding items.');
      return;
    }

    const recordingId = resolveTrackRecordingId(track);
    const rawUri = typeof track.uri === 'string' && track.uri.trim().length ? track.uri.trim() : '';
    const fallbackUri =
      typeof track.id === 'string' && track.id.trim().length
        ? track.id.trim().startsWith('spotify:track:')
          ? track.id.trim()
          : `spotify:track:${track.id.trim()}`
        : '';
    const trackUri = rawUri || fallbackUri;
    const durationMs =
      typeof track.duration_ms === 'number' && Number.isFinite(track.duration_ms)
        ? Math.trunc(track.duration_ms)
        : null;
    const artists =
      Array.isArray(track.artists) && track.artists.length
        ? track.artists
            .map((artist) => (artist && typeof artist.name === 'string' ? artist.name.trim() : ''))
            .filter((name) => name.length > 0)
        : [];
    const albumImageUrl =
      Array.isArray(track.album?.images) && track.album.images.length
        ? track.album.images[0]?.url
        : undefined;

    if (!recordingId && !trackUri) {
      void message.error('No track available to add right now.');
      return;
    }

    setIsQueueAppendPending(true);
    try {
      if (recordingId) {
        await performQueueAction('append-recordings', { recordingIds: [recordingId] });
      } else if (trackUri) {
        await performQueueAction('append-tracks', {
          trackUris: [trackUri],
          trackDurationsMs: [durationMs],
          trackMetadata: [
            {
              title: typeof track.name === 'string' ? track.name.trim() : undefined,
              artists: artists.length ? artists : undefined,
              albumName: track.album?.name,
              albumImageUrl,
              durationMs: durationMs ?? undefined
            }
          ]
        });
      }
      void message.success(t('Added to queue'));
    } catch (error) {
      console.error('Failed to append track to queue', error);
      void message.error('Failed to add to queue');
    } finally {
      setIsQueueAppendPending(false);
    }
  }, [handleUserValidation, isQueueAppendPending, performQueueAction, queueSession, queueStatus, t, track]);

  const options = useMemo(() => {
    return myPlaylists
      .filter((p) => p.id !== playlist?.id)
      .map((p) => {
        return {
          key: p.id,
          label: p.name,
          onClick: () => {
            if (!handleUserValidation()) return;
            playlistService.addPlaylistItems(p!.id, [track.uri], p?.snapshot_id!).then(() => {
              dispatch(playlistActions.refreshTracks(playlist!.id));
              message.open({
                type: 'success',
                content: t('Added to playlist'),
              });
            });
          },
        };
      });
  }, [myPlaylists, playlist, handleUserValidation, track.uri, dispatch, t]);

  const getItems = () => {
    const items: MenuProps['items'] = [
      {
        label: t('Add to playlist'),
        icon: <AddToPlaylist />,
        key: '1',
        children: [
          {
            label: t('New playlist'),
            key: 'new',
            onClick: () => {
              if (!handleUserValidation()) return;
              return playlistService
                .createPlaylist(userId!, { name: track.name })
                .then((response) => {
                  const playlist = response.data;
                  playlistService
                    .addPlaylistItems(playlist.id, [track.uri], playlist.snapshot_id!)
                    .then(() => {
                      dispatch(fetchMyPlaylists());
                      message.success(t('Added to playlist'));
                    });
                });
            },
          },
          { type: 'divider' },
          ...options,
        ],
      },
    ];

    if (saved !== undefined) {
      items.push({
        label: saved ? t('Remove from Liked Songs') : t('Save to Liked Songs'),
        key: '4',
        icon: saved ? (
          <AddedToLibrary style={{ height: 18, width: 18, marginInlineEnd: 0 }} />
        ) : (
          <AddToLibrary style={{ height: 18, width: 18, marginInlineEnd: 0 }} />
        ),
        onClick: () => {
          if (!handleUserValidation(true)) return;
          if (saved) {
            userService.deleteTracks([track.id!]).then(() => {
              dispatch(likedSongsActions.removeSong({ id: track.id! }));
              dispatch(albumActions.updateTrackLikeState({ id: track.id!, saved: false }));
              dispatch(artistActions.setTopSongLikeState({ id: track.id!, saved: false }));
              dispatch(playlistActions.setTrackLikeState({ id: track.id!, saved: false }));

              if (currentSong === track.id) {
                dispatch(spotifyActions.setLiked({ liked: false }));
              }

              if (onSavedToggle) onSavedToggle();
              message.open({
                type: 'success',
                content: t('Removed from Liked Songs'),
              });
            });
          } else {
            userService.saveTracks([track.id!]).then(() => {
              if (onSavedToggle) onSavedToggle();

              dispatch(albumActions.updateTrackLikeState({ id: track.id!, saved: true }));
              dispatch(artistActions.setTopSongLikeState({ id: track.id!, saved: true }));
              dispatch(playlistActions.setTrackLikeState({ id: track.id!, saved: true }));

              dispatch(likedSongsActions.fetchLikeSongs());

              if (currentSong === track.id) {
                dispatch(spotifyActions.setLiked({ liked: true }));
              }

              message.open({
                type: 'success',
                content: t('Saved to Liked Songs'),
              });
            });
          }
        },
      });
    }

    if (canEdit && playlist) {
      items.push({
        label: t('Remove from this playlist'),
        key: '2',
        icon: <DeleteIcon />,
        onClick: () => {
          if (!handleUserValidation()) return;
          return playlistService
            .removePlaylistItems(playlist!.id, [track.uri], playlist?.snapshot_id!)
            .then(() => {
              dispatch(playlistActions.refreshPlaylist(playlist!.id));
              dispatch(playlistActions.removeTrack({ id: track.uri }));
              dispatch(yourLibraryActions.fetchMyPlaylists());
              message.open({
                type: 'success',
                content: t('Removed from playlist'),
              });
            });
        },
      });
    }

    items.push(
      {
        label: t('Add to queue'),
        key: '3',
        icon: <AddToQueueIcon />,
        disabled: isQueueAppendPending,
        onClick: () => {
          void handleQueueAppend();
        },
      },
      { type: 'divider' }
    );

    if (!artist) {
      items.push({
        label: t('Go to artist'),
        key: '5',
        icon: <ArtistIcon />,
        onClick: async () => {
          const primaryArtist = track.artists[0];
          const fallbackDestination = resolveArtistRoute(primaryArtist, composerIds);

          const spotifyArtistId = extractSpotifyArtistId(primaryArtist);
          let destination = fallbackDestination;

          if (spotifyArtistId) {
            const isComposer = await ensureComposerKnown(spotifyArtistId);
            if (isComposer) {
              const catalogId = getCatalogIdForSpotifyId(spotifyArtistId) ?? spotifyArtistId;
              destination = `/composer/${catalogId}`;
            }
          }

          navigate(destination);
        },
      });
    }

    if (!album) {
      items.push({
        label: t('Go to album'),
        key: '6',
        icon: <AlbumIcon />,
        onClick: () => {
          if (!userId) {
            return dispatch(uiActions.openLoginModal(track.album.images[0].url));
          }
          // @ts-ignore
          navigate(`/album/${track.album?.id || track.album.uri.split(':').reverse()[0]}`);
        },
      });
    }

    const recordingId = resolveTrackRecordingId(track);
    if (recordingId) {
      items.push({
        label: t('Go to recording'),
        key: 'go-to-recording',
        icon: <RecordingIcon />,
        onClick: () => {
          navigate(`/recording/${encodeURIComponent(recordingId)}`);
        },
      });
    } else {
      items.push({
        label: (
          <Tooltip title={t('No matching recording yet.')}>
            <span>{t('Recording not known yet')}</span>
          </Tooltip>
        ),
        key: 'go-to-recording',
        icon: <RecordingIcon />,
        disabled: true,
      });
    }

    const workId = resolveTrackWorkId(track);
    if (workId) {
      items.push({
        label: t('Go to work'),
        key: 'go-to-work',
        icon: <WorkIcon />,
        onClick: () => {
          navigate(`/work/${encodeURIComponent(workId)}`);
        },
      });
    } else {
      items.push({
        label: (
          <Tooltip title={t('No matching work yet.')}>
            <span>{t('Work not known yet')}</span>
          </Tooltip>
        ),
        key: 'go-to-work',
        icon: <WorkIcon />,
        disabled: true,
      });
    }

    return items;
  };

  const items = getItems();

  return (
    <>
      <Dropdown menu={{ items }} trigger={props.trigger}>
        {children}
      </Dropdown>
    </>
  );
});
